// Swimlanes (SwimlaneBoard on the Mac): every scene of the arrangement in reading order, one lane per value of a field
// (POV, status, label, setting, section type, a field of the writer's own), "No <field>" last. With Line Up by Reading
// Order the columns are shared across lanes (a column is a moment in the book) under the chapter each scene belongs to;
// off, each lane's cards pack together. Show Empty Lanes gives every value a lane. Drag a card to another lane to set
// its field to that lane's value (one change per scene). Read-only in a collection.
import { useMemo, useState } from 'react';
import type { MetadataValue, TreeNode } from '@writing-suite/core';
import { chapterHeader, laneDropTargets, laneFields, swimlaneBoard, type LaneScene } from '@writing-suite/core/plan';
import { announce } from '../../shell/context.ts';
import type { PageProps } from '../../shell/ProjectShell.tsx';
import { navigate } from '../../shell/route.ts';
import { useWorkspace } from '../../shell/workspace.ts';
import { planEdit, PREF, useSharedPreference } from '../../store/plan.ts';
import { formatNumber } from '../helpers.ts';
import { MenuPopup } from '../Menu.tsx';
import { cardFacts } from './IndexCard.tsx';
import { planItemMenu } from './menus.ts';
import { fadedCard } from '../Corkboard.tsx';

const DRAG_TYPE = 'application/x-writing-suite-fragments';

/** The arrangement's scenes (items with no children) in reading order, each with the folder it's in. */
export function laneScenes(tree: TreeNode[], trashed: (fragmentId: string) => boolean): LaneScene[] {
  const out: LaneScene[] = [];
  const walk = (nodes: TreeNode[], chapter: string | null) => {
    for (const n of nodes) {
      if (n.children.length === 0) {
        if (n.fragmentId && !trashed(n.fragmentId)) out.push({ nodeID: n.id, fragmentID: n.fragmentId, chapter });
      } else walk(n.children, n.title || 'Untitled');
    }
  };
  walk(tree, null);
  return out;
}

export function SwimlaneBoard({ project, tree, node, arrangement, hrefFor, live }: PageProps) {
  const ws = useWorkspace();
  const editable = !!ws?.canStructure && !!live;
  const fields = useMemo(() => laneFields(project.fields()), [project]);
  const [fieldId] = useSharedPreference<string>(PREF.laneField, 'status');
  const [aligned] = useSharedPreference(PREF.lanesAligned, true);
  const [showEmpty] = useSharedPreference(PREF.emptyLanes, false);
  const field = fields.find((f) => f.id === fieldId) ?? fields[0];
  const entities = project.entities();
  const allFields = project.fields();
  const metadataOf = (id: string): Record<string, MetadataValue> | undefined => project.fragment(id)?.metadata;
  const scenes = laneScenes(tree, (id) => project.fragment(id)?.trashed ?? false);
  const lanes = field ? swimlaneBoard(scenes, field, metadataOf, entities, showEmpty) : [];
  const byFragment = new Map(scenes.map((s) => [s.fragmentID, s]));
  const onBoard = new Set(scenes.map((s) => s.fragmentID));
  const headers = chapterHeader(scenes);
  const [target, setTarget] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; id: string } | null>(null);
  const laneKey = (v: string | null) => v ?? '\u0000none';
  const columnsStyle = (el: HTMLElement | null) =>
    el?.style.setProperty('--lane-columns', aligned ? String(Math.max(1, scenes.length)) : '1');

  const card = (fragmentId: string, column: number) => {
    const scene = byFragment.get(fragmentId);
    if (!scene) return null;
    const n: TreeNode = { id: scene.nodeID, fragmentId, title: project.fragment(fragmentId)?.title ?? 'Untitled', isFolder: false, children: [] };
    const f = cardFacts(project, n, allFields, entities);
    const selected = node?.id === scene.nodeID;
    const writeHref = hrefFor('write', scene.nodeID);
    return (
      <li
        key={fragmentId}
        className={`lane-card${selected ? ' selected' : ''}${fadedCard(ws, n)}`}
        data-fragment-id={fragmentId}
        data-node-id={scene.nodeID}
        draggable={editable}
        ref={(el) => {
          if (aligned) el?.style.setProperty('grid-column', String(column + 1));
          else el?.style.removeProperty('grid-column');
        }}
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData(DRAG_TYPE, JSON.stringify([fragmentId]));
          e.dataTransfer.setData('text/plain', fragmentId);
        }}
        onContextMenu={(e) => {
          if (!ws) return;
          e.preventDefault();
          setMenu({ x: e.clientX, y: e.clientY, id: scene.nodeID });
        }}
      >
        <a
          href={writeHref}
          draggable={false}
          aria-current={selected ? 'true' : undefined}
          onClick={(e) => {
            if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            e.preventDefault();
            navigate(hrefFor('plan/corkboard', scene.nodeID));
          }}
          onDoubleClick={() => navigate(writeHref)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              navigate(writeHref);
            }
          }}
        >
          {f.edge ? <span className="card-edge" aria-hidden="true" ref={(el) => el?.style.setProperty('--edge', f.edge)} /> : null}
          <span className="lane-card-title">{n.title || 'Untitled'}</span>
          <span className="lane-card-foot">
            <span>{f.status}</span>
            <span className="card-words">{f.words > 0 ? formatNumber(f.words) : ''}</span>
          </span>
        </a>
      </li>
    );
  };

  return (
    <div className="corkboard-page swimlanes-page" data-layout="swimlanes">
      <header className="page-head">
        <h1 tabIndex={-1}>{arrangement.name}</h1>
        <p className="page-sub">{field ? `Lanes by ${field.name} · ${scenes.length === 1 ? '1 scene' : `${scenes.length} scenes`}` : ''}</p>
      </header>
      {!field ? (
        <p className="empty-note">No fields to group by. Swimlanes group scenes by a field with a set of values, such as POV or Status.</p>
      ) : scenes.length === 0 ? (
        <p className="empty-note">No scenes.</p>
      ) : (
        <div className="swimlanes-scroll" role="region" aria-label={`Swimlanes by ${field.name}`} tabIndex={0}>
          <div className={`swimlanes${aligned ? ' aligned' : ''}`} ref={columnsStyle}>
            {aligned ? (
              <div className="lane chapter-row" aria-hidden="true">
                <span className="lane-title" />
                <ol className="lane-cards">
                  {headers.map((h, i) => (
                    <li key={i} className={`lane-chapter${h ? ' starts' : ''}`} ref={(el) => el?.style.setProperty('grid-column', String(i + 1))}>
                      {h}
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}
            {lanes.map((lane) => (
              <section
                key={laneKey(lane.value)}
                className={`lane${target === laneKey(lane.value) ? ' drop-target' : ''}`}
                data-lane={lane.value ?? ''}
                aria-label={`${lane.title}, ${lane.cards.length === 1 ? '1 scene' : `${lane.cards.length} scenes`}`}
                onDragOver={(e) => {
                  if (!editable || !e.dataTransfer.types.includes(DRAG_TYPE)) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (target !== laneKey(lane.value)) setTarget(laneKey(lane.value));
                }}
                onDragLeave={(e) => {
                  if (target === laneKey(lane.value) && !(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) setTarget(null);
                }}
                onDrop={(e) => {
                  setTarget(null);
                  if (!editable || !live) return;
                  let dropped: string[] = [];
                  try {
                    dropped = JSON.parse(e.dataTransfer.getData(DRAG_TYPE) || '[]') as string[];
                  } catch {
                    return;
                  }
                  e.preventDefault();
                  const ids = laneDropTargets(dropped, lane, field.id, onBoard, metadataOf);
                  if (!ids.length) return;
                  planEdit(live, (ed) => ed.dropOnLane(ids, field.id, lane.value));
                  announce(`${field.name}: ${lane.value ? lane.title : 'none'}`);
                }}
              >
                <h2 className="lane-title">{lane.title}</h2>
                <ol className="lane-cards">{lane.cards.map((c) => card(c.id, c.column))}</ol>
              </section>
            ))}
          </div>
        </div>
      )}
      {menu && ws ? (
        <MenuPopup
          label="Card"
          at={menu}
          entries={[
            { label: 'Open in Write', onSelect: () => navigate(hrefFor('write', menu.id)) },
            { kind: 'separator' },
            ...planItemMenu(ws, [menu.id], { onRename: (id) => ws.setRenaming(id) }),
          ]}
          onClose={() => setMenu(null)}
        />
      ) : null}
    </div>
  );
}
